import { Component } from '@angular/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { Share } from '@capacitor/share';
import { Browser } from '@capacitor/browser';
import { SplashScreen } from '@capacitor/splash-screen';
import { FilePicker } from '@capawesome/capacitor-file-picker';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';

interface TestResult {
  name: string;
  status: 'pending' | 'success' | 'error';
  message: string;
  data?: any;
}

@Component({
  selector: 'capacitor-test-page',
  templateUrl: 'capacitor-test.html'
})
export class CapacitorTestPage {

  testResults: TestResult[] = [];
  isRunning = false;
  capacitorVersion = '7.x';

  constructor(
    private customNavCtrl: CustomNavController
  ) {
    this.initializeTests();
  }

  initializeTests() {
    this.testResults = [
      { name: 'Device Info', status: 'pending', message: 'Not tested yet' },
      { name: 'Network Status', status: 'pending', message: 'Not tested yet' },
      { name: 'App Info', status: 'pending', message: 'Not tested yet' },
      { name: 'Geolocation', status: 'pending', message: 'Not tested yet' },
      { name: 'Camera', status: 'pending', message: 'Not tested yet' },
      { name: 'File Picker', status: 'pending', message: 'Not tested yet' },
      { name: 'Share', status: 'pending', message: 'Not tested yet' },
      { name: 'Browser', status: 'pending', message: 'Not tested yet' },
      { name: 'Status Bar', status: 'pending', message: 'Not tested yet' },
      { name: 'Keyboard', status: 'pending', message: 'Not tested yet' },
      { name: 'Splash Screen', status: 'pending', message: 'Not tested yet' }
    ];
  }

  goBack() {
    this.customNavCtrl.back();
  }

  async runAllTests() {
    this.isRunning = true;
    this.initializeTests();

    await this.testDeviceInfo();
    await this.testNetworkStatus();
    await this.testAppInfo();
    await this.testStatusBar();
    await this.testKeyboard();
    await this.testSplashScreen();

    this.isRunning = false;
  }

  async testDeviceInfo() {
    const testIndex = 0;
    try {
      const info = await Device.getInfo();
      this.testResults[testIndex] = {
        name: 'Device Info',
        status: 'success',
        message: `Platform: ${info.platform}, OS: ${info.operatingSystem} ${info.osVersion}`,
        data: info
      };
    } catch (error) {
      this.testResults[testIndex] = {
        name: 'Device Info',
        status: 'error',
        message: error.message || 'Failed to get device info'
      };
    }
  }

  async testNetworkStatus() {
    const testIndex = 1;
    try {
      const status = await Network.getStatus();
      this.testResults[testIndex] = {
        name: 'Network Status',
        status: 'success',
        message: `Connected: ${status.connected}, Type: ${status.connectionType}`,
        data: status
      };
    } catch (error) {
      this.testResults[testIndex] = {
        name: 'Network Status',
        status: 'error',
        message: error.message || 'Failed to get network status'
      };
    }
  }

  async testAppInfo() {
    const testIndex = 2;
    try {
      const info = await App.getInfo();
      this.testResults[testIndex] = {
        name: 'App Info',
        status: 'success',
        message: `Name: ${info.name}, Version: ${info.version}, Build: ${info.build}`,
        data: info
      };
    } catch (error) {
      this.testResults[testIndex] = {
        name: 'App Info',
        status: 'error',
        message: error.message || 'Failed to get app info'
      };
    }
  }

  async testGeolocation() {
    const testIndex = 3;
    try {
      const position = await Geolocation.getCurrentPosition();
      this.testResults[testIndex] = {
        name: 'Geolocation',
        status: 'success',
        message: `Lat: ${position.coords.latitude.toFixed(4)}, Lng: ${position.coords.longitude.toFixed(4)}`,
        data: position
      };
    } catch (error) {
      this.testResults[testIndex] = {
        name: 'Geolocation',
        status: 'error',
        message: error.message || 'Failed to get location. Make sure permissions are granted.'
      };
    }
  }

  async testCamera() {
    const testIndex = 4;
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera
      });
      this.testResults[testIndex] = {
        name: 'Camera',
        status: 'success',
        message: `Photo captured: ${image.format}`,
        data: { webPath: image.webPath }
      };
    } catch (error) {
      if (error.message && error.message.includes('User cancelled')) {
        this.testResults[testIndex] = {
          name: 'Camera',
          status: 'pending',
          message: 'User cancelled photo capture'
        };
      } else {
        this.testResults[testIndex] = {
          name: 'Camera',
          status: 'error',
          message: error.message || 'Failed to capture photo'
        };
      }
    }
  }

  async testFilePicker() {
    const testIndex = 5;
    try {
      const result = await FilePicker.pickFiles({
        types: ['image/*']
      });
      this.testResults[testIndex] = {
        name: 'File Picker',
        status: 'success',
        message: `File selected: ${result.files[0]?.name || 'Unknown'}`,
        data: result
      };
    } catch (error) {
      if (error.message && error.message.includes('cancelled')) {
        this.testResults[testIndex] = {
          name: 'File Picker',
          status: 'pending',
          message: 'User cancelled file selection'
        };
      } else {
        this.testResults[testIndex] = {
          name: 'File Picker',
          status: 'error',
          message: error.message || 'Failed to pick file'
        };
      }
    }
  }

  async testShare() {
    const testIndex = 6;
    try {
      await Share.share({
        title: 'Capacitor 7 Test',
        text: 'Testing Capacitor 7 Share API on Android 15',
        url: 'https://capacitorjs.com',
        dialogTitle: 'Share Test'
      });
      this.testResults[testIndex] = {
        name: 'Share',
        status: 'success',
        message: 'Share dialog opened successfully'
      };
    } catch (error) {
      this.testResults[testIndex] = {
        name: 'Share',
        status: 'error',
        message: error.message || 'Failed to open share dialog'
      };
    }
  }

  async testBrowser() {
    const testIndex = 7;
    try {
      await Browser.open({ url: 'https://capacitorjs.com' });
      this.testResults[testIndex] = {
        name: 'Browser',
        status: 'success',
        message: 'Browser opened successfully'
      };
    } catch (error) {
      this.testResults[testIndex] = {
        name: 'Browser',
        status: 'error',
        message: error.message || 'Failed to open browser'
      };
    }
  }

  async testStatusBar() {
    const testIndex = 8;
    try {
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.show();
      this.testResults[testIndex] = {
        name: 'Status Bar',
        status: 'success',
        message: 'Status bar style updated successfully'
      };
    } catch (error) {
      this.testResults[testIndex] = {
        name: 'Status Bar',
        status: 'error',
        message: error.message || 'Failed to update status bar'
      };
    }
  }

  async testKeyboard() {
    const testIndex = 9;
    try {
      await Keyboard.show();
      setTimeout(async () => {
        await Keyboard.hide();
      }, 1000);
      this.testResults[testIndex] = {
        name: 'Keyboard',
        status: 'success',
        message: 'Keyboard show/hide working'
      };
    } catch (error) {
      this.testResults[testIndex] = {
        name: 'Keyboard',
        status: 'error',
        message: error.message || 'Failed to control keyboard'
      };
    }
  }

  async testSplashScreen() {
    const testIndex = 10;
    try {
      await SplashScreen.hide();
      this.testResults[testIndex] = {
        name: 'Splash Screen',
        status: 'success',
        message: 'Splash screen API working'
      };
    } catch (error) {
      this.testResults[testIndex] = {
        name: 'Splash Screen',
        status: 'error',
        message: error.message || 'Failed to control splash screen'
      };
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'pending': return '⏳';
      default: return '❓';
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'danger';
      case 'pending': return 'warning';
      default: return 'medium';
    }
  }
}
